// Centralized CMP dashboard display formatters (defect ledger SH-004/SH-009).
// Display dates across manage tables and detail pages use the long form
// "January 01, 2026". Do not re-declare per-page Intl formatters — import
// these instead so the whole dashboard stays consistent.

export const cmpLongDateFormatter = new Intl.DateTimeFormat("en-CA", {
  month: "long",
  day: "2-digit",
  year: "numeric",
});

export const cmpShortDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const cmpShortTimeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

export const cmpShortDateTimeFormatter = {
  format: (value: number | Date): string =>
    `${cmpShortDateFormatter.format(value)} at ${cmpShortTimeFormatter.format(value)}`,
};

// Renewal dates and other calendar-only values are stored as UTC instants,
// but their day is the business value. Always render those values in UTC so
// midnight UTC does not become the prior day in negative-offset timezones.
// True timestamps (for example, "last updated") must continue using the
// regular local-time formatters above and below.
export const cmpLongUtcCalendarDateFormatter = new Intl.DateTimeFormat(
  "en-CA",
  {
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }
);

export const cmpLongDateTimeFormatter = new Intl.DateTimeFormat("en-CA", {
  month: "long",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export const cmpTimeFormatter = new Intl.DateTimeFormat("en-CA", {
  hour: "numeric",
  minute: "2-digit",
});

export const formatLongDate = (value: number | Date): string =>
  cmpLongDateFormatter.format(value instanceof Date ? value : new Date(value));

export const formatUtcCalendarDate = (
  value?: number | Date | null
): string => {
  if (value === undefined || value === null) {
    return "-";
  }

  return cmpLongUtcCalendarDateFormatter.format(
    value instanceof Date ? value : new Date(value)
  );
};

const getOrdinalSuffix = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return "th";
  return ["th", "st", "nd", "rd"][day % 10] || "th";
};

/**
 * Status-card timestamp in the design's "Sept 30th, 2026" form, shown in the
 * chamber time zone. September uses "Sept" as the design does.
 */
export const formatOrdinalShortDate = (
  value: number | Date,
  timeZone?: string
): string => {
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).formatToParts(value instanceof Date ? value : new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value || "";
  const month = part("month") === "Sep" ? "Sept" : part("month");
  const day = Number(part("day"));
  return `${month} ${day}${getOrdinalSuffix(day)}, ${part("year")}`;
};

export const getUtcCalendarDateStartUtcMilli = (dateInput: string): number =>
  new Date(`${dateInput}T00:00:00.000Z`).getTime();

export const getUtcCalendarDateEndUtcMilli = (dateInput: string): number =>
  new Date(`${dateInput}T23:59:59.999Z`).getTime();

const parseCalendarDate = (dateInput: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput);

  if (!match) {
    throw new Error(`Invalid calendar date: ${dateInput}`);
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const getTimeZoneOffsetUtcMilli = ({
  utcMilli,
  timeZone,
}: {
  utcMilli: number;
  timeZone: string;
}): number => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(utcMilli));
  const valueByType = new Map(parts.map((part) => [part.type, part.value]));
  const representedAsUtc = Date.UTC(
    Number(valueByType.get("year")),
    Number(valueByType.get("month")) - 1,
    Number(valueByType.get("day")),
    Number(valueByType.get("hour")),
    Number(valueByType.get("minute")),
    Number(valueByType.get("second"))
  );

  return representedAsUtc - Math.floor(utcMilli / 1000) * 1000;
};

const localMidnightToUtcMilli = ({
  dateInput,
  timeZone,
}: {
  dateInput: string;
  timeZone: string;
}): number => {
  const { year, month, day } = parseCalendarDate(dateInput);
  const wallClockAsUtc = Date.UTC(year, month - 1, day);
  let utcGuess = wallClockAsUtc;

  // Re-evaluate the offset because the first guess can fall on the opposite
  // side of a daylight-saving transition.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const nextGuess =
      wallClockAsUtc -
      getTimeZoneOffsetUtcMilli({ utcMilli: utcGuess, timeZone });
    if (nextGuess === utcGuess) {
      break;
    }
    utcGuess = nextGuess;
  }

  return utcGuess;
};

export const getTimeZoneCalendarDateStartUtcMilli = (
  dateInput: string,
  timeZone: string
): number => localMidnightToUtcMilli({ dateInput, timeZone });

export const getTimeZoneCalendarDateEndUtcMilli = (
  dateInput: string,
  timeZone: string
): number => {
  const { year, month, day } = parseCalendarDate(dateInput);
  const nextCalendarDate = new Date(Date.UTC(year, month - 1, day + 1))
    .toISOString()
    .slice(0, 10);

  return (
    localMidnightToUtcMilli({
      dateInput: nextCalendarDate,
      timeZone,
    }) - 1
  );
};

export const cmpCurrencyFormatter = new Intl.NumberFormat("en-CA", {
  currency: "CAD",
  style: "currency",
});

export const formatCurrencyFromCents = (amountInCents?: number | null): string =>
  cmpCurrencyFormatter.format((amountInCents || 0) / 100);

export const formatCurrencyFromDollars = (amountInDollars?: number | null): string =>
  cmpCurrencyFormatter.format(amountInDollars || 0);
